import * as Xlsx from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  return isNaN(d.getTime())
    ? dateStr
    : d.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
};

export const downloadAltLedgerExcel = (processedList, filterType = "All") => {
  let dataToExport = processedList;
  if (filterType === "Entries") {
    dataToExport = processedList.filter(
      (doc) => doc.type === "entry" || doc.recordType === "entry"
    );
  } else if (filterType === "Deductions") {
    dataToExport = processedList.filter(
      (doc) => doc.type === "deduction" || doc.recordType === "deduction"
    );
  }

  const data = dataToExport.map((doc) => {
    const isDeduction =
      doc.type === "deduction" || doc.recordType === "deduction";
    const q = isDeduction ? 1 : Number(doc.qty || 1);
    const sAmt = isDeduction ? -Number(doc.amount || 0) : Number(doc.amount || 0) * q;
    const cAmt = isDeduction ? 0 : Number(doc.customerAmount || 0) * q;
    const profit = (!isDeduction && doc.category === "customer") ? cAmt - (Number(doc.amount || 0) * q) : 0;

    return {
      Date: formatDate(doc.created || doc.createdAt),
      Type: isDeduction ? "Deduction / Clear (-)" : "Entry (+)",
      Category: doc.category ? (doc.category === "shop" ? "Shop" : "Customer") : "-",
      PhoneOrRef: doc.phone || (isDeduction ? "Clear" : ""),
      Qty: isDeduction ? "-" : (doc.qty || 1),
      Customer: doc.customer || "",
      CustomerPhone: doc.customerPhone || "",
      IssueOrNote: doc.issue || doc.note || "",
      SunilAmount: sAmt,
      CustomerAmount: cAmt,
      Profit: profit,
      Cleared: isDeduction ? "" : (doc.isCleared ? "Yes" : "No"),
      ClearId: doc.clearId || "",
      ClearBatchID: doc.clearBatchId || "",
      ClearedAt: doc.clearedAt || "",
      BatchID: doc.clearBatchId || "",
      CreatedBy: doc.createdBy || "",
    };
  });

  const workSheet = Xlsx.utils.json_to_sheet(data);
  const workBook = Xlsx.utils.book_new();
  Xlsx.utils.book_append_sheet(workBook, workSheet, "Alt Ledger");
  const dateStr = new Date().toISOString().slice(0, 10);
  Xlsx.writeFile(
    workBook,
    `alt-ledger-${filterType.toLowerCase()}-${dateStr}.xlsx`
  );
};

export const downloadAltAnalyticsExcel = (processedList) => {
  const data = processedList.map((doc) => {
    const isDeduction =
      doc.type === "deduction" || doc.recordType === "deduction";
    const q = isDeduction ? 1 : Number(doc.qty || 1);
    const sAmt = isDeduction ? -Number(doc.amount || 0) : Number(doc.amount || 0) * q;
    const cAmt = isDeduction ? 0 : Number(doc.customerAmount || 0) * q;
    const profit = (!isDeduction && doc.category === "customer") ? cAmt - (Number(doc.amount || 0) * q) : 0;

    return {
      Date: formatDate(doc.created || doc.createdAt),
      Type: isDeduction ? "Deduction / Clear (-)" : "Entry (+)",
      Category: doc.category ? (doc.category === "shop" ? "Shop" : "Customer") : "-",
      PhoneOrRef: doc.phone || (isDeduction ? "Clear" : ""),
      Qty: isDeduction ? "-" : (doc.qty || 1),
      Customer: doc.customer || "",
      CustomerPhone: doc.customerPhone || "",
      IssueOrNote: doc.issue || doc.note || "",
      SunilAmount: sAmt,
      CustomerAmount: cAmt,
      Profit: profit,
      Cleared: isDeduction ? "" : (doc.isCleared ? "Yes" : "No"),
      ClearId: doc.clearId || "",
      ClearBatchID: doc.clearBatchId || "",
      ClearedAt: doc.clearedAt || "",
      BatchID: doc.clearBatchId || "",
      CreatedBy: doc.createdBy || "",
    };
  });

  const workSheet = Xlsx.utils.json_to_sheet(data);
  const workBook = Xlsx.utils.book_new();
  Xlsx.utils.book_append_sheet(workBook, workSheet, "Period Analytics");
  const dateStr = new Date().toISOString().slice(0, 10);
  Xlsx.writeFile(workBook, `alt-analytics-period-${dateStr}.xlsx`);
};

export const exportAltCustomData = (allItems = [], options = {}) => {
  const {
    format = "excel", // "excel" | "pdf"
    scope = "all", // "all" | "uncleared" | "cleared" | "deductions"
    category = "all", // "all" | "shop" | "customer"
    includeDeductions = true,
    startDate,
    endDate,
  } = options;

  let filtered = [...allItems];

  // 1. Scope filter
  if (scope === "uncleared") {
    filtered = filtered.filter((doc) => {
      const isDeduction = doc.recordType === "deduction" || doc.type === "deduction";
      if (isDeduction) return includeDeductions;
      return !doc.isCleared;
    });
  } else if (scope === "cleared") {
    filtered = filtered.filter((doc) => {
      const isDeduction = doc.recordType === "deduction" || doc.type === "deduction";
      if (isDeduction) return includeDeductions;
      return !!doc.isCleared;
    });
  } else if (scope === "deductions") {
    filtered = filtered.filter(
      (doc) => doc.recordType === "deduction" || doc.type === "deduction"
    );
  } else if (scope === "all") {
    if (!includeDeductions) {
      filtered = filtered.filter(
        (doc) => doc.recordType !== "deduction" && doc.type !== "deduction"
      );
    }
  }

  // 2. Category filter
  if (category === "shop") {
    filtered = filtered.filter((doc) => doc.category === "shop" || doc.recordType === "deduction");
  } else if (category === "customer") {
    filtered = filtered.filter((doc) => doc.category === "customer" || doc.recordType === "deduction");
  }

  // 3. Date range filter
  if (startDate) {
    const sTime = new Date(`${startDate}T00:00:00`).getTime();
    filtered = filtered.filter((doc) => {
      const t = new Date(doc.created || doc.createdAt || 0).getTime();
      return t >= sTime;
    });
  }
  if (endDate) {
    const eTime = new Date(`${endDate}T23:59:59`).getTime();
    filtered = filtered.filter((doc) => {
      const t = new Date(doc.created || doc.createdAt || 0).getTime();
      return t <= eTime;
    });
  }

  let totalShopTotal = 0;
  let totalSunilC = 0;
  let totalCustomer = 0;
  let totalProfit = 0;

  const exportData = filtered.map((doc) => {
    const isDeduction = doc.type === "deduction" || doc.recordType === "deduction";

    let shopTotal = 0;
    let sunilC = 0;
    let customerTotal = 0;
    let profit = 0;

    if (isDeduction) {
      shopTotal = -Number(doc.clearedShopTotal ?? doc.clearedShop ?? doc.amount ?? 0);
      sunilC = -Number(doc.clearedSunilC ?? doc.clearedSunil ?? 0);
      customerTotal = -Number(doc.clearedCustomerTotal ?? doc.previousCustomerTotal ?? 0);
      profit = -Number(doc.clearedProfit ?? doc.previousProfit ?? (Math.abs(customerTotal) - Math.abs(sunilC)));
    } else {
      const q = Number(doc.qty || 1);
      const sUnit = Number(doc.amount || 0);
      const cUnit = Number(doc.customerAmount || 0);

      if (doc.category === "shop") {
        shopTotal = sUnit * q;
      } else if (doc.category === "customer") {
        sunilC = sUnit * q;
      }
      customerTotal = cUnit * q;
      profit = customerTotal - sunilC;
    }

    totalShopTotal += shopTotal;
    totalSunilC += sunilC;
    totalCustomer += customerTotal;
    totalProfit += profit;

    return {
      Date: formatDate(doc.created || doc.createdAt),
      Type: isDeduction ? "Deduction (-)" : "Entry (+)",
      Category: doc.category ? (doc.category === "shop" ? "Shop" : "Customer") : "-",
      PhoneOrRef: doc.phone || (isDeduction ? "Clear" : ""),
      Qty: isDeduction ? "-" : (doc.qty || 1),
      Customer: doc.customer || "",
      CustomerPhone: doc.customerPhone || "",
      IssueOrNote: doc.issue || doc.note || "",
      ShopTotal: shopTotal,
      SunilC: sunilC,
      CustomerTotal: customerTotal,
      Profit: profit,
      Cleared: isDeduction ? "" : (doc.isCleared ? "Yes" : "No"),
      ClearId: doc.clearId || "",
      ClearBatchID: doc.clearBatchId || "",
      ClearedAt: doc.clearedAt ? formatDate(doc.clearedAt) : "",
      CreatedBy: doc.createdBy || "",
    };
  });

  const dateStr = new Date().toISOString().slice(0, 10);

  if (format === "pdf") {
    const doc = new jsPDF({ orientation: "landscape" });
    doc.setFontSize(15);
    doc.text("Alt Entries Custom Export Report", 14, 14);
    doc.setFontSize(9);
    doc.text(
      `Generated: ${new Date().toLocaleString("en-IN")} | Scope: ${scope} | Category: ${category}`,
      14,
      20
    );
    doc.text(
      `Totals -> Shop Total: Rs. ${Math.ceil(totalShopTotal).toLocaleString("en-IN")} | Sunil C: Rs. ${Math.ceil(totalSunilC).toLocaleString("en-IN")} | Customer: Rs. ${Math.ceil(totalCustomer).toLocaleString("en-IN")} | Profit: Rs. ${Math.ceil(totalProfit).toLocaleString("en-IN")}`,
      14,
      26
    );

    const head = [
      [
        "Date",
        "Type",
        "Category",
        "Phone / Ref",
        "Qty",
        "Customer",
        "Shop Total",
        "Sunil C",
        "Customer Total",
        "Profit",
        "Cleared",
      ],
    ];

    const body = exportData.map((d) => [
      d.Date,
      d.Type,
      d.Category,
      d.PhoneOrRef,
      d.Qty,
      d.Customer,
      `Rs. ${Math.ceil(d.ShopTotal).toLocaleString("en-IN")}`,
      `Rs. ${Math.ceil(d.SunilC).toLocaleString("en-IN")}`,
      `Rs. ${Math.ceil(d.CustomerTotal).toLocaleString("en-IN")}`,
      `Rs. ${Math.ceil(d.Profit).toLocaleString("en-IN")}`,
      d.Cleared,
    ]);

    // Append summary totals row
    body.push([
      "TOTAL",
      "-",
      "-",
      "-",
      "-",
      "-",
      `Rs. ${Math.ceil(totalShopTotal).toLocaleString("en-IN")}`,
      `Rs. ${Math.ceil(totalSunilC).toLocaleString("en-IN")}`,
      `Rs. ${Math.ceil(totalCustomer).toLocaleString("en-IN")}`,
      `Rs. ${Math.ceil(totalProfit).toLocaleString("en-IN")}`,
      "-",
    ]);

    autoTable(doc, {
      startY: 30,
      head,
      body,
      theme: "striped",
      styles: { fontSize: 8 },
      headStyles: { fillColor: [255, 56, 92] },
    });

    doc.save(`alt-custom-export-${scope}-${category}-${dateStr}.pdf`);
  } else {
    // Append summary totals object for Excel export
    const excelExportData = [
      ...exportData,
      {
        Date: "TOTAL",
        Type: "-",
        Category: "-",
        PhoneOrRef: "-",
        Qty: "-",
        Customer: "-",
        CustomerPhone: "-",
        IssueOrNote: "-",
        ShopTotal: totalShopTotal,
        SunilC: totalSunilC,
        CustomerTotal: totalCustomer,
        Profit: totalProfit,
        Cleared: "-",
        ClearId: "-",
        ClearBatchID: "-",
        ClearedAt: "-",
        CreatedBy: "-",
      },
    ];

    const workSheet = Xlsx.utils.json_to_sheet(excelExportData);
    const workBook = Xlsx.utils.book_new();
    Xlsx.utils.book_append_sheet(workBook, workSheet, "Custom Export");
    Xlsx.writeFile(workBook, `alt-custom-export-${scope}-${category}-${dateStr}.xlsx`);
  }
};
